/**
 * Protocol Wizard
 *
 * A 3-step wizard for creating annual meeting protocols (årsstämmoprotokoll)
 * according to ABL 7 kap 48-53§.
 *
 * Steps:
 * 1. Meeting Details - Date, time, place, officers
 * 2. Attendees - Shareholder list with representation
 * 3. Decisions - All resolutions from the meeting
 */

import { useState, useEffect, useCallback } from "react";
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  Vote,
  FileText,
  ChevronRight,
  ChevronLeft,
  Check,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/common/Modal";
import {
  protocolsApi,
  agendasApi,
  type ProtocolPrefillData,
  type ProtocolAttendee,
  type ProtocolDecisions,
  type ProtocolDischargeDecision,
  type CreateProtocolData,
  type UpdateProtocolData,
  type Protocol,
  type AgendaWithDetails,
} from "@/lib/api";

interface ProtocolWizardProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  annualReportId?: string;
  agendaId?: string; // Link to sent agenda for prefilling meeting details
  existingProtocol?: Protocol; // For editing mode
  onSuccess?: (protocolId: string) => void;
}

type WizardStep = 1 | 2 | 3;

interface MeetingDetails {
  meetingDate: string;
  meetingTime: string;
  meetingPlace: string;
  chairpersonName: string;
  secretaryName: string;
  adjusterName: string;
}

export function ProtocolWizard({
  isOpen,
  onClose,
  companyId,
  annualReportId,
  agendaId,
  existingProtocol,
  onSuccess,
}: ProtocolWizardProps) {
  const isEditMode = !!existingProtocol;
  const [step, setStep] = useState<WizardStep>(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [prefillData, setPrefillData] = useState<ProtocolPrefillData | null>(null);

  // Selected annual report (can be changed by user if not pre-selected)
  const [selectedAnnualReportId, setSelectedAnnualReportId] = useState<string | undefined>(annualReportId);

  // Step 1: Meeting Details
  const [meetingDetails, setMeetingDetails] = useState<MeetingDetails>({
    meetingDate: new Date().toISOString().split("T")[0],
    meetingTime: "10:00",
    meetingPlace: "",
    chairpersonName: "",
    secretaryName: "",
    adjusterName: "",
  });

  // Step 2: Attendees
  const [attendees, setAttendees] = useState<ProtocolAttendee[]>([]);

  // Step 3: Decisions
  const [decisions, setDecisions] = useState<ProtocolDecisions>({
    financialStatementsApproved: true,
    profitDisposition: undefined,
    discharge: [],
    boardElection: { performed: true, members: [] },
    auditorElection: { performed: false, auditor: "" },
  });

  const [otherBusiness, setOtherBusiness] = useState("");

  // Load prefill data or existing protocol data
  useEffect(() => {
    if (!isOpen) return;

    const loadData = async () => {
      setIsLoading(true);
      try {
        // If editing, pre-fill from existing protocol
        if (existingProtocol) {
          setMeetingDetails({
            meetingDate: existingProtocol.meetingDate,
            meetingTime: existingProtocol.meetingTime || "10:00",
            meetingPlace: existingProtocol.meetingPlace || "",
            chairpersonName: existingProtocol.chairpersonName,
            secretaryName: existingProtocol.secretaryName || "",
            adjusterName: existingProtocol.adjusterName || "",
          });

          const existingAttendees = (existingProtocol.attendees || []) as ProtocolAttendee[];
          setAttendees(existingAttendees);

          const existingDecisions = (existingProtocol.decisions || {}) as ProtocolDecisions;

          setOtherBusiness(existingProtocol.otherBusiness || "");

          // Load prefill data for company info AND fresh profit disposition from annual report
          const response = await protocolsApi.getPrefill(companyId, existingProtocol.annualReportId || undefined);
          if (response.success && response.data) {
            setPrefillData(response.data);

            // For draft protocols, ALWAYS use fresh profit disposition from the annual report
            // This ensures changes in the annual report are reflected in the protocol
            const freshProfitDisposition = response.data.annualReport?.profitDisposition;

            setDecisions({
              financialStatementsApproved: existingDecisions.financialStatementsApproved ?? true,
              // Use fresh data from annual report if available, otherwise fall back to stored decisions
              profitDisposition: freshProfitDisposition || existingDecisions.profitDisposition,
              discharge: existingDecisions.discharge || [],
              boardElection: existingDecisions.boardElection || { performed: true, members: [] },
              auditorElection: existingDecisions.auditorElection || { performed: false, auditor: "" },
            });
          } else {
            // If prefill fails, use stored decisions
            setDecisions({
              financialStatementsApproved: existingDecisions.financialStatementsApproved ?? true,
              profitDisposition: existingDecisions.profitDisposition,
              discharge: existingDecisions.discharge || [],
              boardElection: existingDecisions.boardElection || { performed: true, members: [] },
              auditorElection: existingDecisions.auditorElection || { performed: false, auditor: "" },
            });
          }
        } else {
          // Creating new - load prefill data
          const response = await protocolsApi.getPrefill(companyId, selectedAnnualReportId);
          if (response.success && response.data) {
            setPrefillData(response.data);

            // If no annual report is pre-selected, auto-select the first available one
            if (!selectedAnnualReportId && response.data.availableAnnualReports) {
              const firstAvailable = response.data.availableAnnualReports.find((r) => !r.hasProtocol);
              if (firstAvailable) {
                setSelectedAnnualReportId(firstAvailable.id);
                // We'll reload with the selected annual report
                return;
              }
            }

            // If agendaId is provided, fetch agenda details and prefill meeting info
            let agendaData: AgendaWithDetails | null = null;
            if (agendaId) {
              try {
                const agendaResponse = await agendasApi.get(companyId, agendaId);
                if (agendaResponse.success && agendaResponse.data) {
                  agendaData = agendaResponse.data.agenda;
                }
              } catch (err) {
                console.error("Failed to load agenda:", err);
              }
            }

            // Pre-fill meeting details from agenda (if available) or company seat
            if (agendaData) {
              setMeetingDetails((prev) => ({
                ...prev,
                meetingDate: agendaData!.meetingDate,
                meetingTime: agendaData!.meetingTime || "10:00",
                meetingPlace: agendaData!.meetingPlace || response.data!.company.seatMunicipality || "",
              }));

              // Transfer custom agenda items to otherBusiness
              const customAgendaItems = (agendaData.items || [])
                .filter((item) => item.itemType === "custom")
                .map((item) => {
                  if (item.description) {
                    return `${item.title}\n${item.description}`;
                  }
                  return item.title;
                });

              if (customAgendaItems.length > 0) {
                setOtherBusiness(customAgendaItems.join("\n\n"));
              }
            } else if (response.data.company.seatMunicipality) {
              setMeetingDetails((prev) => ({
                ...prev,
                meetingPlace: response.data!.company.seatMunicipality || "",
              }));
            }

            // Pre-fill attendees from shareholders
            const shareholderAttendees: ProtocolAttendee[] = response.data.shareholders.map((s) => ({
              name: s.name,
              shares: s.totalShares,
              votes: s.totalVotes,
              present: true,
              representedBy: undefined,
            }));
            setAttendees(shareholderAttendees);

            // Pre-fill profit disposition from annual report
            if (response.data.annualReport?.profitDisposition) {
              setDecisions((prev) => ({
                ...prev,
                profitDisposition: response.data!.annualReport!.profitDisposition,
              }));
            }

            // Pre-fill discharge from board members
            const dischargeDecisions: ProtocolDischargeDecision[] =
              response.data.boardMembers.map((m) => ({
                name: m.name,
                role: m.role,
                granted: true,
              }));
            setDecisions((prev) => ({
              ...prev,
              discharge: dischargeDecisions,
            }));

            // Pre-fill board election
            setDecisions((prev) => ({
              ...prev,
              boardElection: {
                performed: true,
                members: response.data!.boardMembers,
              },
            }));
          }
        }
      } catch (error) {
        console.error("Failed to load data:", error);
        toast.error("Kunde inte ladda data");
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isOpen, companyId, selectedAnnualReportId, existingProtocol, agendaId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setSelectedAnnualReportId(annualReportId);
      setMeetingDetails({
        meetingDate: new Date().toISOString().split("T")[0],
        meetingTime: "10:00",
        meetingPlace: "",
        chairpersonName: "",
        secretaryName: "",
        adjusterName: "",
      });
      setAttendees([]);
      setDecisions({
        financialStatementsApproved: true,
        profitDisposition: undefined,
        discharge: [],
        boardElection: { performed: true, members: [] },
        auditorElection: { performed: false, auditor: "" },
      });
      setOtherBusiness("");
    }
  }, [isOpen, annualReportId]);

  const validateStep1 = useCallback((): boolean => {
    // Require annual report selection for new protocols
    if (!isEditMode && !selectedAnnualReportId) {
      toast.error("Välj en årsredovisning att fastställa");
      return false;
    }
    if (!meetingDetails.meetingDate) {
      toast.error("Ange mötesdatum");
      return false;
    }
    if (!meetingDetails.chairpersonName.trim()) {
      toast.error("Ange ordförandens namn");
      return false;
    }
    return true;
  }, [meetingDetails, selectedAnnualReportId, isEditMode]);

  const validateStep2 = useCallback((): boolean => {
    const presentAttendees = attendees.filter((a) => a.present || a.representedBy);
    if (presentAttendees.length === 0) {
      toast.error("Minst en deltagare måste vara närvarande");
      return false;
    }
    return true;
  }, [attendees]);

  const handleNext = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    if (step < 3) setStep((step + 1) as WizardStep);
  };

  const handleBack = () => {
    if (step > 1) setStep((step - 1) as WizardStep);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const data: CreateProtocolData = {
        annualReportId: existingProtocol?.annualReportId || selectedAnnualReportId,
        agendaId: agendaId || undefined, // Link to agenda if creating from sent agenda
        type: "annual_meeting",
        meetingDate: meetingDetails.meetingDate,
        meetingTime: meetingDetails.meetingTime || undefined,
        meetingPlace: meetingDetails.meetingPlace || undefined,
        chairpersonName: meetingDetails.chairpersonName,
        secretaryName: meetingDetails.secretaryName || undefined,
        adjusterName: meetingDetails.adjusterName || undefined,
        attendees,
        decisions,
        otherBusiness: otherBusiness || undefined,
      };

      let response;
      if (isEditMode && existingProtocol) {
        // Update existing protocol - only include fields that can be updated
        const updateData: UpdateProtocolData = {
          meetingDate: meetingDetails.meetingDate,
          meetingTime: meetingDetails.meetingTime || undefined,
          meetingPlace: meetingDetails.meetingPlace || undefined,
          chairpersonName: meetingDetails.chairpersonName,
          secretaryName: meetingDetails.secretaryName || undefined,
          adjusterName: meetingDetails.adjusterName || undefined,
          attendees,
          decisions,
          otherBusiness: otherBusiness || undefined,
        };
        response = await protocolsApi.update(companyId, existingProtocol.id, updateData);
      } else {
        // Create new protocol
        response = await protocolsApi.create(companyId, data);
      }

      if (response.success && response.data) {
        toast.success(isEditMode ? "Protokoll uppdaterat!" : "Protokoll skapat!");
        onSuccess?.(response.data.protocol.id);
        onClose();
      } else {
        toast.error(response.error?.message || (isEditMode ? "Kunde inte uppdatera protokoll" : "Kunde inte skapa protokoll"));
      }
    } catch (error) {
      console.error(`Failed to ${isEditMode ? "update" : "create"} protocol:`, error);
      toast.error("Ett fel uppstod");
    } finally {
      setIsSaving(false);
    }
  };

  // Calculate totals
  const totalShares = attendees.reduce((sum, a) => sum + a.shares, 0);
  const representedShares = attendees
    .filter((a) => a.present || a.representedBy)
    .reduce((sum, a) => sum + a.shares, 0);
  const representedVotes = attendees
    .filter((a) => a.present || a.representedBy)
    .reduce((sum, a) => sum + a.votes, 0);

  const stepTitles: Record<WizardStep, string> = {
    1: "Mötesuppgifter",
    2: "Deltagarlista",
    3: "Beslut",
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-primary-600" />
          <span>{isEditMode ? "Redigera årsstämmoprotokoll" : "Skapa årsstämmoprotokoll"}</span>
        </div>
      }
      size="2xl"
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Progress Steps */}
          <div className="flex items-center justify-between">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    s === step
                      ? "bg-primary-600 text-white"
                      : s < step
                        ? "bg-green-500 text-white"
                        : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {s < step ? <Check className="w-4 h-4" /> : s}
                </div>
                <span
                  className={`ml-2 text-sm font-medium ${
                    s === step ? "text-gray-900" : "text-gray-500"
                  }`}
                >
                  {stepTitles[s as WizardStep]}
                </span>
                {s < 3 && (
                  <ChevronRight
                    className={`w-5 h-5 mx-4 ${s < step ? "text-green-500" : "text-gray-300"}`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Company Info */}
          {prefillData && (
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm font-medium text-gray-900">{prefillData.company.name}</p>
              <p className="text-xs text-gray-500">Org.nr: {prefillData.company.organizationNumber}</p>
            </div>
          )}

          {/* Step Content */}
          <div className="min-h-[300px]">
            {step === 1 && (
              <Step1MeetingDetails
                details={meetingDetails}
                onChange={setMeetingDetails}
                prefillData={prefillData}
                selectedAnnualReportId={selectedAnnualReportId}
                onAnnualReportChange={setSelectedAnnualReportId}
                isEditMode={isEditMode}
              />
            )}
            {step === 2 && (
              <Step2Attendees
                attendees={attendees}
                onChange={setAttendees}
                totalShares={totalShares}
                representedShares={representedShares}
                representedVotes={representedVotes}
              />
            )}
            {step === 3 && (
              <Step3Decisions
                decisions={decisions}
                onChange={setDecisions}
                otherBusiness={otherBusiness}
                onOtherBusinessChange={setOtherBusiness}
              />
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={step === 1 ? onClose : handleBack}
              className="btn btn-secondary"
              disabled={isSaving}
            >
              {step === 1 ? "Avbryt" : (
                <>
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Tillbaka
                </>
              )}
            </button>

            {step < 3 ? (
              <button type="button" onClick={handleNext} className="btn btn-primary">
                Fortsätt
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="btn btn-primary flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sparar...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    {isEditMode ? "Spara ändringar" : "Skapa protokoll"}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Step 1: Meeting Details
// ═══════════════════════════════════════════════════════════════════

interface Step1Props {
  details: MeetingDetails;
  onChange: (details: MeetingDetails) => void;
  prefillData: ProtocolPrefillData | null;
  selectedAnnualReportId?: string;
  onAnnualReportChange: (id: string) => void;
  isEditMode: boolean;
}

function Step1MeetingDetails({
  details,
  onChange,
  prefillData,
  selectedAnnualReportId,
  onAnnualReportChange,
  isEditMode,
}: Step1Props) {
  const updateField = (field: keyof MeetingDetails, value: string) => {
    onChange({ ...details, [field]: value });
  };

  // Get board members for quick selection
  const boardMembers = prefillData?.boardMembers || [];

  // Get available annual reports (those without protocols)
  const availableReports = prefillData?.availableAnnualReports?.filter((r) => !r.hasProtocol) || [];
  const reportsWithProtocols = prefillData?.availableAnnualReports?.filter((r) => r.hasProtocol) || [];

  // Format fiscal year for display
  const formatFiscalYear = (start: string, end: string) => {
    return `${start} – ${end}`;
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-600">
        Ange uppgifter om årsstämman. Välj årsredovisning, datum och ordförande.
      </p>

      {/* Annual Report Selection */}
      {!isEditMode && (
        <div className="p-4 bg-primary-50 rounded-lg border border-primary-200">
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            <FileText className="w-4 h-4 inline mr-1" />
            Årsredovisning att fastställa *
          </label>
          {availableReports.length > 0 ? (
            <>
              <select
                value={selectedAnnualReportId || ""}
                onChange={(e) => onAnnualReportChange(e.target.value)}
                className="w-full px-3 py-2 border border-primary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
              >
                <option value="">Välj årsredovisning...</option>
                {availableReports.map((r) => (
                  <option key={r.id} value={r.id}>
                    Räkenskapsår {formatFiscalYear(r.fiscalYearStart, r.fiscalYearEnd)}
                    {r.status === "completed" || r.status === "exported" ? " ✓" : " (utkast)"}
                  </option>
                ))}
              </select>
              {reportsWithProtocols.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  {reportsWithProtocols.length} årsredovisning(ar) har redan protokoll
                </p>
              )}
            </>
          ) : (
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Ingen tillgänglig årsredovisning</p>
                  <p className="text-xs mt-1">
                    {reportsWithProtocols.length > 0
                      ? "Alla årsredovisningar har redan protokoll."
                      : "Skapa först en årsredovisning för att kunna upprätta protokoll."}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Show selected annual report info */}
      {prefillData?.annualReport && (
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-600">
            <span className="font-medium">Räkenskapsår:</span>{" "}
            {formatFiscalYear(prefillData.annualReport.fiscalYearStart, prefillData.annualReport.fiscalYearEnd)}
          </p>
          {prefillData.deadlines && (
            <p className="text-xs text-gray-500 mt-1">
              Årsstämma senast: {prefillData.deadlines.annualMeetingDeadline}
            </p>
          )}
        </div>
      )}

      {/* Date and Time */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Calendar className="w-4 h-4 inline mr-1" />
            Datum *
          </label>
          <input
            type="date"
            value={details.meetingDate}
            onChange={(e) => updateField("meetingDate", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Clock className="w-4 h-4 inline mr-1" />
            Tid
          </label>
          <input
            type="time"
            value={details.meetingTime}
            onChange={(e) => updateField("meetingTime", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Place */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <MapPin className="w-4 h-4 inline mr-1" />
          Plats
        </label>
        <input
          type="text"
          value={details.meetingPlace}
          onChange={(e) => updateField("meetingPlace", e.target.value)}
          placeholder="T.ex. Bolagets kontor, Stockholm"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Officers */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-gray-900">Funktionärer</h4>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Ordförande *
          </label>
          {boardMembers.length > 0 && (
            <div className="mb-2">
              <select
                value={details.chairpersonName}
                onChange={(e) => updateField("chairpersonName", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
              >
                <option value="">Välj från styrelsen...</option>
                {boardMembers.map((m) => (
                  <option key={m.name} value={m.name}>
                    {m.name} ({m.role})
                  </option>
                ))}
              </select>
            </div>
          )}
          <input
            type="text"
            value={details.chairpersonName}
            onChange={(e) => updateField("chairpersonName", e.target.value)}
            placeholder={boardMembers.length > 0 ? "Eller skriv in annat namn" : "Namn på ordförande"}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sekreterare
          </label>
          <input
            type="text"
            value={details.secretaryName}
            onChange={(e) => updateField("secretaryName", e.target.value)}
            placeholder="Namn på sekreterare (valfritt)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Justeringsman
          </label>
          <input
            type="text"
            value={details.adjusterName}
            onChange={(e) => updateField("adjusterName", e.target.value)}
            placeholder="Namn på justeringsman (rekommenderas)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Step 2: Attendees
// ═══════════════════════════════════════════════════════════════════

interface Step2Props {
  attendees: ProtocolAttendee[];
  onChange: (attendees: ProtocolAttendee[]) => void;
  totalShares: number;
  representedShares: number;
  representedVotes: number;
}

function Step2Attendees({
  attendees,
  onChange,
  totalShares,
  representedShares,
  representedVotes,
}: Step2Props) {
  const togglePresent = (index: number) => {
    const updated = [...attendees];
    updated[index] = { ...updated[index], present: !updated[index].present };
    if (updated[index].present) {
      updated[index].representedBy = undefined;
    }
    onChange(updated);
  };

  const setRepresentedBy = (index: number, value: string) => {
    const updated = [...attendees];
    updated[index] = {
      ...updated[index],
      representedBy: value || undefined,
      present: value ? false : updated[index].present,
    };
    onChange(updated);
  };

  const representedPercent = totalShares > 0 ? (representedShares / totalShares) * 100 : 0;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Markera vilka aktieägare som är närvarande eller företrädda genom ombud.
      </p>

      {/* Summary */}
      <div className="p-3 bg-primary-50 rounded-lg border border-primary-200">
        <div className="flex items-center gap-4 text-sm">
          <div>
            <span className="text-gray-600">Representerade aktier:</span>
            <span className="ml-1 font-semibold text-primary-700">
              {representedShares.toLocaleString("sv-SE")} / {totalShares.toLocaleString("sv-SE")}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Röster:</span>
            <span className="ml-1 font-semibold text-primary-700">
              {representedVotes.toLocaleString("sv-SE")}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Andel:</span>
            <span className="ml-1 font-semibold text-primary-700">
              {representedPercent.toFixed(1)}%
            </span>
          </div>
        </div>
      </div>

      {/* Attendee List */}
      {attendees.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>Inga aktieägare hittades</p>
          <p className="text-xs mt-1">
            Lägg till aktieägare i aktieboken för att de ska visas här
          </p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {attendees.map((attendee, index) => (
            <div
              key={index}
              className={`p-3 rounded-lg border transition-colors ${
                attendee.present || attendee.representedBy
                  ? "bg-green-50 border-green-200"
                  : "bg-gray-50 border-gray-200"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{attendee.name}</p>
                  <p className="text-xs text-gray-500">
                    {attendee.shares.toLocaleString("sv-SE")} aktier,{" "}
                    {attendee.votes.toLocaleString("sv-SE")} röster
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={attendee.present}
                      onChange={() => togglePresent(index)}
                      className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Närvarande</span>
                  </label>

                  <div className="w-px h-6 bg-gray-300" />

                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Ombud:</span>
                    <input
                      type="text"
                      value={attendee.representedBy || ""}
                      onChange={(e) => setRepresentedBy(index, e.target.value)}
                      placeholder="Namn på ombud"
                      className="w-32 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Warning if less than 100% */}
      {representedPercent < 100 && (
        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            Alla aktier är inte representerade. Kontrollera att samtliga närvarande aktieägare
            är markerade.
          </p>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Step 3: Decisions
// ═══════════════════════════════════════════════════════════════════

interface Step3Props {
  decisions: ProtocolDecisions;
  onChange: (decisions: ProtocolDecisions) => void;
  otherBusiness: string;
  onOtherBusinessChange: (value: string) => void;
}

function Step3Decisions({
  decisions,
  onChange,
  otherBusiness,
  onOtherBusinessChange,
}: Step3Props) {
  const formatCurrency = (value: number) =>
    value.toLocaleString("sv-SE", { style: "currency", currency: "SEK", maximumFractionDigits: 0 });

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-600">
        Ange de beslut som fattades på stämman. Förval baseras på årsredovisningens uppgifter.
      </p>

      {/* Financial Statements */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={decisions.financialStatementsApproved}
            onChange={(e) =>
              onChange({ ...decisions, financialStatementsApproved: e.target.checked })
            }
            className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
          />
          <div>
            <span className="font-medium text-gray-900">
              Fastställande av resultat- och balansräkning
            </span>
            <p className="text-xs text-gray-500">
              Stämman fastställde bolagets resultat- och balansräkning för räkenskapsåret
            </p>
          </div>
        </label>
      </div>

      {/* Profit Disposition */}
      {decisions.profitDisposition && (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="font-medium text-gray-900 mb-3">
            <Vote className="w-4 h-4 inline mr-1" />
            Resultatdisposition
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Balanserat resultat:</span>
              <span className="font-medium">
                {formatCurrency(decisions.profitDisposition.retainedEarnings)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Årets resultat:</span>
              <span className="font-medium">
                {formatCurrency(decisions.profitDisposition.currentYearResult)}
              </span>
            </div>
            <div className="border-t border-gray-300 pt-2 mt-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Utdelning:</span>
                <span className="font-medium">
                  {formatCurrency(decisions.profitDisposition.dividend)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">I ny räkning:</span>
                <span className="font-medium">
                  {formatCurrency(decisions.profitDisposition.carriedForward)}
                </span>
              </div>
            </div>
          </div>
          <label className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
            <input
              type="checkbox"
              checked={decisions.profitDisposition.followsBoardProposal}
              onChange={(e) =>
                onChange({
                  ...decisions,
                  profitDisposition: {
                    ...decisions.profitDisposition!,
                    followsBoardProposal: e.target.checked,
                  },
                })
              }
              className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">I enlighet med styrelsens förslag</span>
          </label>
        </div>
      )}

      {/* Discharge */}
      {decisions.discharge && decisions.discharge.length > 0 && (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="font-medium text-gray-900 mb-3">Ansvarsfrihet</h4>
          <div className="space-y-2">
            {decisions.discharge.map((d, index) => (
              <label key={index} className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={d.granted}
                  onChange={(e) => {
                    const updated = [...decisions.discharge!];
                    updated[index] = { ...updated[index], granted: e.target.checked };
                    onChange({ ...decisions, discharge: updated });
                  }}
                  className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">
                  {d.name} ({d.role})
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Board Election */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <label className="flex items-center gap-3 mb-3">
          <input
            type="checkbox"
            checked={decisions.boardElection?.performed}
            onChange={(e) =>
              onChange({
                ...decisions,
                boardElection: {
                  ...decisions.boardElection!,
                  performed: e.target.checked,
                },
              })
            }
            className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
          />
          <span className="font-medium text-gray-900">Val av styrelse</span>
        </label>

        {decisions.boardElection?.performed && decisions.boardElection.members.length > 0 && (
          <div className="ml-7 space-y-1">
            {decisions.boardElection.members.map((m, index) => (
              <p key={index} className="text-sm text-gray-600">
                {m.name} - {m.role}
              </p>
            ))}
          </div>
        )}
      </div>

      {/* Auditor Election */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <label className="flex items-center gap-3 mb-3">
          <input
            type="checkbox"
            checked={decisions.auditorElection?.performed}
            onChange={(e) =>
              onChange({
                ...decisions,
                auditorElection: {
                  ...decisions.auditorElection!,
                  performed: e.target.checked,
                },
              })
            }
            className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
          />
          <span className="font-medium text-gray-900">Val av revisor</span>
        </label>

        {decisions.auditorElection?.performed && (
          <div className="ml-7">
            <input
              type="text"
              value={decisions.auditorElection?.auditor || ""}
              onChange={(e) =>
                onChange({
                  ...decisions,
                  auditorElection: {
                    ...decisions.auditorElection!,
                    auditor: e.target.value,
                  },
                })
              }
              placeholder="Revisorns namn"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        )}
      </div>

      {/* Other Business */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Övriga ärenden
        </label>
        <textarea
          value={otherBusiness}
          onChange={(e) => onOtherBusinessChange(e.target.value)}
          placeholder="Eventuella övriga ärenden som behandlades på stämman..."
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>
    </div>
  );
}

export default ProtocolWizard;
